import { z } from "zod";

/**
 * `COMMITTEE-CARD-CONTRACT`: **the committee card of `GET /api/v1/bank/decisions`, `committee`, one contract for the door and for every client** (A48: a stub speaks the provider's contract, never the client's
 * expectation). The card was in NO contract: the door answered `Promise<unknown>` and bank-web read it by hand (`routes/comite.tsx` `committeeFilesFrom`), keeping the keys it needed and dropping the rest.
 * Strict on purpose, as `bank-decision-answer.ts` is, with the same lenient READ twin built from the same shapes ({@link buildSchemas}): the strict schema is for stubs and the parity spec, the twin is what a
 * client parses the live answer with (extra keys tolerated at every level, every required field and type identical).
 *
 * The rest of the queue answer (`pending`, `recent`, `indicators`) is not contracted here.
 */
function buildSchemas(object: typeof z.strictObject) {
  const text = z.string();
  const wholeAmount = z.number().int();

  /** Why a file sits before the committee, as the server kept it (`CommitteeReason` of `domain-types`; the same shape as `bank-decision-answer.ts`'s `committee.reasons`). */
  const committeeCardReasonSchema = z.discriminatedUnion("kind", [
    object({ kind: z.literal("amount"), amountFinanced: wholeAmount, thresholdAmount: wholeAmount }),
    object({
      kind: z.literal("rule"),
      code: z.enum([
        "rateBelowGrid",
        "gridRateMissing",
        "durationAboveMax",
        "vehicleAgeAtMaturity",
        "ltvAboveMax",
      ]),
      rule: text,
      detail: text,
    }),
  ]);

  /** One file waiting for the séance, with its figures (`COMITE-RECORD-1`, `COMMITTEE-AMOUNT`, `CHEF-SELF-VALIDATION` R2). */
  const committeeCardFileSchema = object({
    bankFileId: text,
    submissionId: text,
    reference: text,
    clientName: text,
    /** The amount the threshold was compared to: everything lent (`COMMITTEE-AMOUNT`, D-1025-9). */
    amountFinanced: wholeAmount,
    waitingDays: z.number().int().min(0),
    /** `CHEF-SELF-VALIDATION` R2 (frame 193): true when the chef sent her own file to the comité before any offer left. Served, never inferred. */
    beforeOffer: z.boolean(),
    reasons: z.array(committeeCardReasonSchema),
    /** The day of the last ajournement (`AAAA-MM-JJ`), `null` when the file was never adjourned. */
    adjournedOn: text.nullable(),
    adjournments: z.number().int().min(0),
  });

  /** `intelligence`'s counsel for the séance, composed at every read from the waiting files' own figures; `null` when none wait. */
  const committeeCounselSchema = object({
    sentence: text,
    lines: z.array(
      object({
        bankFileId: text,
        reference: text,
        clientName: text,
        figures: text,
        grade: text.nullable(),
        avis: z.enum(["favorable", "reserve", "defavorable"]).nullable(),
      }),
    ),
  });

  const bankCommitteeCardSchema = object({
    waiting: z.number().int().min(0),
    files: z.array(committeeCardFileSchema),
    counsel: committeeCounselSchema.nullable(),
    /** The card says what it cannot say (the séances are phase 3's), in its own words. */
    sessionsDeferred: text,
  });

  return {
    committeeCardReasonSchema,
    committeeCardFileSchema,
    committeeCounselSchema,
    bankCommitteeCardSchema,
  };
}

const strict = buildSchemas(z.strictObject);
const loose = buildSchemas(z.looseObject);

export const committeeCardFileSchema = strict.committeeCardFileSchema;
export const committeeCounselSchema = strict.committeeCounselSchema;
export const bankCommitteeCardSchema = strict.bankCommitteeCardSchema;

/** The READ twin: what bank-web parses the live card with. */
export const bankCommitteeCardReadSchema = loose.bankCommitteeCardSchema;

export type BankCommitteeCard = z.infer<typeof bankCommitteeCardSchema>;
export type CommitteeCardFile = z.infer<typeof committeeCardFileSchema>;
export type CommitteeCounsel = z.infer<typeof committeeCounselSchema>;
